import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify-icon/react";

import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import useWorkspaceInformationStore from "../../store/useWorkspaceInformationStore";
import { unblockWorkspace } from "../../api/workspaceSettings";

import { SettingsCard } from "./SettingsCard";
import { BlockWorkspaceDialog } from "./BlockWorkspaceDialog.tsx";

import { Button } from "@/components/ui/button";
import { toast } from "@/utils/customToast";
import useUserStore from "@/features/auth/store/useUserStore";

export const WorkspaceBlockingCard = () => {
  const { t } = useTranslation(["workspaces"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { settings, refetch } = useWorkspaceSettingsStore();
  const { refetch: refetchWorkspaceInfo } = useWorkspaceInformationStore();
  const { user } = useUserStore();
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);

  if (!settings || !user) return null;

  const { members } = settings;
  const currentUserId = user.id;

  // Find the current user in the members array
  const currentUserMember = members.find((member) => member.id === currentUserId);

  if (!currentUserMember) return null;

  // Check if workspace is blocked by the current user (self-blocked)
  const isSelfBlocked =
    currentUserMember.blocked_at && currentUserMember.blocked_by === currentUserId;
  // Check if workspace is blocked by someone else
  const isBlockedByOthers =
    currentUserMember.blocked_at && currentUserMember.blocked_by !== currentUserId;
  // Check if user is a student
  const isStudent = user.role === "student";

  const getErrorMessage = (errorCode: string, errorMessage?: string) => {
    const errorMap: Record<string, string> = {
      not_workspace_member: t(
        "settings.blockWorkspace.errors.notWorkspaceMember",
        "You are not a member of this workspace.",
      ),
      user_not_blocked_workspace: t(
        "settings.blockWorkspace.errors.notBlocked",
        "You have not blocked this workspace.",
      ),
      user_blocked_by_others: t(
        "settings.blockWorkspace.errors.blockedByOthersUnblock",
        "You cannot unblock this workspace because you have been blocked by someone else.",
      ),
      not_authenticated: t(
        "settings.blockWorkspace.errors.notAuthenticated",
        "Authentication required",
      ),
      insufficient_permissions: t(
        "settings.blockWorkspace.errors.insufficientPermissions",
        "Insufficient permissions",
      ),
    };

    return (
      errorMap[errorCode] ||
      errorMessage ||
      t("settings.blockWorkspace.errors.generic", "An error occurred while processing your request")
    );
  };

  const handleUnblock = async () => {
    if (!workspaceId) return;

    setIsUnblocking(true);
    try {
      await unblockWorkspace(workspaceId);

      // Refetch settings to get updated blocking status
      await refetch(workspaceId);

      // Refetch workspace basics data
      await refetchWorkspaceInfo(workspaceId);
    } catch (error: any) {
      console.error("Failed to unblock workspace:", error);

      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.message;
      const errorMsg = getErrorMessage(errorCode, errorMessage);

      toast.error(errorMsg);
    } finally {
      setIsUnblocking(false);
    }
  };

  const getBlockingInfo = () => {
    if (isBlockedByOthers) {
      return {
        icon: "mdi:block-helper",
        title: t("settings.blockWorkspace.blockedByOthers", "Blocked by Others"),
        description:
          currentUserMember.blocked_reason ||
          t(
            "settings.blockWorkspace.blockedByOthersDescription",
            "This workspace has been blocked by someone else.",
          ),
        topCircle: "from-red-600 to-pink-500",
        bottomCircle: "from-red-500 to-orange-500",
        showUnblockButton: false,
        showBlockButton: false,
      };
    }

    if (isSelfBlocked) {
      return {
        icon: "mdi:block-helper",
        title: t("settings.blockWorkspace.blockedBySelf", "Blocked by You"),
        description:
          currentUserMember.blocked_reason ||
          t("settings.blockWorkspace.blockedBySelfDescription", "You have blocked this workspace."),
        topCircle: "from-orange-600 to-red-500",
        bottomCircle: "from-orange-500 to-pink-500",
        showUnblockButton: isStudent, // Only show unblock button for students when self-blocked
        showBlockButton: false,
      };
    }

    return {
      icon: "mdi:shield-check",
      title: t("settings.blockWorkspace.notBlocked", "Not Blocked"),
      description: t(
        "settings.blockWorkspace.notBlockedDescription",
        "This workspace is not blocked.",
      ),
      topCircle: "from-green-600 to-blue-500",
      bottomCircle: "from-green-500 to-teal-500",
      showUnblockButton: false,
      showBlockButton: isStudent, // Only show block button for students
    };
  };

  const blockingInfo = getBlockingInfo();

  // Only show the card for students when they can take action
  // Hide card when blocked by others since students can't do anything
  if (isBlockedByOthers) {
    return null;
  }

  const action = (
    <div className="flex items-center gap-2">
      {blockingInfo.showBlockButton && (
        <Button
          className="h-6 gap-1 px-2 text-xs"
          size="sm"
          variant="destructive"
          onClick={() => setIsBlockDialogOpen(true)}
        >
          <Icon icon="mdi:block-helper" />
          {t("settings.blockWorkspace.blockButton", "Block Workspace")}
        </Button>
      )}
      {blockingInfo.showUnblockButton && (
        <Button
          className="h-6 gap-1 px-2 text-xs"
          disabled={isUnblocking}
          size="sm"
          variant="foreground"
          onClick={handleUnblock}
        >
          {isUnblocking ? (
            <Icon className="animate-spin" icon="lucide:loader-2" />
          ) : (
            <Icon icon="mdi:shield-check" />
          )}
          {t("settings.blockWorkspace.unblockButton", "Unblock Workspace")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <SettingsCard
        action={action}
        bottomCircle={blockingInfo.bottomCircle}
        bottomCirclePlacement="bottom-left"
        bottomCircleSize="h-20 w-20"
        description={blockingInfo.description}
        icon={blockingInfo.icon}
        title={blockingInfo.title}
        topCircle={blockingInfo.topCircle}
        topCirclePlacement="top-right"
        topCircleSize="h-16 w-16"
      />

      <BlockWorkspaceDialog
        isOpen={isBlockDialogOpen}
        onClose={() => setIsBlockDialogOpen(false)}
      />
    </>
  );
};
